import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Scale, FileText, ChevronDown, Shield, Eye, Globe, Server, AlertTriangle, CheckCircle2, Mail, Download, BookOpen } from 'lucide-react';
interface TermSection {
  id: string;
  title: string;
  icon: React.ElementType;
  content: React.ReactNode;
}
const TERMS_SECTIONS: TermSection[] = [{
  id: 'generales',
  title: '1. Términos Generales del Servicio',
  icon: FileText,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6709">
        <p data-id="element-6710">
          Al acceder y utilizar nuestra plataforma de gestión de proyectos con
          agentes de IA (el "Servicio"), usted acepta estar sujeto a estos
          Términos y Condiciones. Si no está de acuerdo con alguna parte de los
          términos, no podrá acceder al Servicio.
        </p>
        <p data-id="element-6711">
          El Servicio está diseñado para equipos y organizaciones. Usted declara
          y garantiza que tiene la autoridad legal para vincular a su
          organización a estos términos.
        </p>
        <p data-id="element-6712">
          Nos reservamos el derecho de suspender o cancelar su cuenta si
          determinamos que ha violado estos términos o ha utilizado el Servicio
          de manera fraudulenta o ilegal.
        </p>
      </div>
}, {
  id: 'uso-aceptable',
  title: '2. Uso Aceptable',
  icon: CheckCircle2,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6713">
        <p data-id="element-6714">
          Usted se compromete a utilizar el agente de IA y las herramientas de
          la plataforma de manera responsable. Está estrictamente prohibido:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6715">
          <li data-id="element-6716">Utilizar el Servicio para fines ilegales o no autorizados.</li>
          <li data-id="element-6717">
            Intentar eludir las limitaciones de la API o los sistemas de
            facturación.
          </li>
          <li data-id="element-6718">
            Ingresar prompts o instrucciones diseñadas para comprometer la
            seguridad del agente (prompt injection).
          </li>
          <li data-id="element-6719">
            Sobrecargar intencionalmente nuestra infraestructura o la de
            nuestros proveedores integrados.
          </li>
        </ul>
      </div>
}, {
  id: 'integraciones',
  title: '3. Integraciones de Terceros',
  icon: Globe,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6720">
        <p data-id="element-6721">
          El Servicio se integra con plataformas de terceros como Google
          Workspace, Jira, GitHub y Slack mediante nuestro MCP (Model Context
          Protocol) Gateway.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6722">
          <li data-id="element-6723">
            <strong data-id="element-6724">Google Workspace:</strong> El acceso a Gmail, Calendar y
            Drive se realiza a través de OAuth. Los datos fluyen a través de
            nuestro MCP Gateway para ser procesados por el agente.
          </li>
          <li data-id="element-6725">
            <strong data-id="element-6726">Responsabilidad:</strong> No somos responsables por la
            disponibilidad o los cambios en las APIs de estos proveedores
            externos.
          </li>
          <li data-id="element-6727">
            <strong data-id="element-6728">Permisos:</strong> Usted es responsable de otorgar y revocar
            los permisos adecuados en las plataformas de terceros.
          </li>
        </ul>
      </div>
}, {
  id: 'privacidad',
  title: '4. Privacidad y Datos',
  icon: Shield,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6729">
        <p data-id="element-6730">
          La seguridad de sus datos es nuestra prioridad. Nuestro enfoque de
          arquitectura garantiza la máxima protección:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6731">
          <li data-id="element-6732">
            <strong data-id="element-6733">Almacenamiento de Tokens:</strong> Los tokens de acceso
            (OAuth) nunca se almacenan en el navegador del usuario. Se guardan
            de forma segura en AWS Secrets Manager.
          </li>
          <li data-id="element-6734">
            <strong data-id="element-6735">Procesamiento de IA:</strong> Los datos de sus integraciones
            (emails, tickets, código) se envían a los modelos de lenguaje (LLMs)
            únicamente para procesar sus consultas específicas y no se utilizan
            para entrenar modelos fundacionales.
          </li>
          <li data-id="element-6736">
            <strong data-id="element-6737">Retención:</strong> Mantenemos un historial de chat cifrado
            para la continuidad del contexto, el cual puede ser eliminado por el
            usuario en cualquier momento.
          </li>
        </ul>
      </div>
}, {
  id: 'propiedad',
  title: '5. Propiedad Intelectual',
  icon: Eye,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6738">
        <p data-id="element-6739">
          <strong data-id="element-6740">Su contenido:</strong> Usted retiene todos los derechos de
          propiedad sobre los datos, código y documentos que procesa a través de
          nuestro Servicio.
        </p>
        <p data-id="element-6741">
          <strong data-id="element-6742">Nuestra plataforma:</strong> El código fuente, diseño,
          arquitectura del agente, prompts del sistema y la marca de la
          plataforma son propiedad exclusiva de nuestra empresa y están
          protegidos por leyes de propiedad intelectual.
        </p>
      </div>
}, {
  id: 'sla',
  title: '6. Disponibilidad del Servicio (SLA)',
  icon: Server,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6743">
        <p data-id="element-6744">
          Nos esforzamos por mantener un tiempo de actividad (uptime) del 99.9%
          para nuestros servicios principales.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6745">
          <li data-id="element-6746">
            <strong data-id="element-6747">Mantenimiento:</strong> Las ventanas de mantenimiento
            programado se notificarán con al menos 48 horas de anticipación.
          </li>
          <li data-id="element-6748">
            <strong data-id="element-6749">Dependencias:</strong> Nuestro SLA no cubre interrupciones
            causadas por caídas en los proveedores de LLM (ej. OpenAI,
            Anthropic) o APIs de terceros (Google, Atlassian).
          </li>
        </ul>
      </div>
}, {
  id: 'responsabilidad',
  title: '7. Limitación de Responsabilidad',
  icon: AlertTriangle,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6750">
        <p data-id="element-6751">
          El Servicio, incluyendo las respuestas generadas por IA, se
          proporciona "tal cual". No garantizamos la exactitud absoluta de las
          sugerencias de código, resúmenes o acciones tomadas por el agente.
        </p>
        <p data-id="element-6752">
          En ningún caso seremos responsables por daños indirectos,
          incidentales, especiales o consecuentes, incluyendo pérdida de
          beneficios, datos o uso, que surjan de o estén relacionados con el uso
          del Servicio.
        </p>
      </div>
}, {
  id: 'modificaciones',
  title: '8. Modificaciones a los Términos',
  icon: BookOpen,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6753">
        <p data-id="element-6754">
          Nos reservamos el derecho de modificar o reemplazar estos Términos en
          cualquier momento. Si una revisión es material, intentaremos
          proporcionar un aviso con al menos 30 días de anticipación antes de
          que los nuevos términos entren en vigencia.
        </p>
        <p data-id="element-6755">
          El uso continuo del Servicio después de que las modificaciones entren
          en vigencia constituye su aceptación de los nuevos Términos.
        </p>
      </div>
}, {
  id: 'contacto',
  title: '9. Contacto',
  icon: Mail,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6756">
        <p data-id="element-6757">
          Si tiene alguna pregunta sobre estos Términos y Condiciones, por favor
          contáctenos a través de:
        </p>
        <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 inline-block" data-id="element-6758">
          <p className="font-mono text-indigo-400" data-id="element-6759">legal@agent.ai</p>
        </div>
      </div>
}];
export function TermsPanel() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    generales: true
  });
  const toggleSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const expandAll = () => {
    const allOpen = TERMS_SECTIONS.reduce((acc, section) => {
      acc[section.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setOpenSections(allOpen);
  };
  const collapseAll = () => {
    setOpenSections({});
  };
  const scrollToSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: true
    }));
    setTimeout(() => {
      const element = document.getElementById(`term-section-${id}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-6760">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-6761">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-6762">
          <div data-id="element-6763">
            <div className="flex items-center gap-3 mb-2" data-id="element-6764">
              <div className="p-2.5 bg-indigo-950/50 rounded-xl border border-indigo-900/50" data-id="element-6765">
                <Scale size={24} className="text-indigo-400" data-id="element-6766" />
              </div>
              <h1 className="text-3xl font-bold text-slate-100" data-id="element-6767">
                Términos y Condiciones
              </h1>
            </div>
            <p className="text-slate-400 mt-2" data-id="element-6768">
              Acuerdo legal para el uso de la plataforma y agentes de IA.
            </p>
            <div className="flex items-center gap-3 mt-4" data-id="element-6769">
              <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-300" data-id="element-6770">
                Versión 2.1
              </Badge>
              <span className="text-xs text-slate-500" data-id="element-6771">
                Última actualización: 15 de Octubre, 2026
              </span>
            </div>
          </div>
          <div className="shrink-0" data-id="element-6772">
            <Button variant="outline" className="gap-2 bg-slate-900 border-slate-700 text-slate-300 hover:text-slate-100 hover:bg-slate-800" data-id="element-6773">
              <Download size={16} data-id="element-6774" />
              Descargar PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-6775">
          {/* Table of Contents (Sidebar) */}
          <div className="lg:col-span-1" data-id="element-6776">
            <div className="sticky top-8 space-y-4" data-id="element-6777">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-6778">
                Contenido
              </h3>
              <nav className="space-y-1 border-l-2 border-slate-800" data-id="element-6779">
                {TERMS_SECTIONS.map(section => <button key={`toc-${section.id}`} onClick={() => scrollToSection(section.id)} className={`w-full text-left px-4 py-2 text-sm transition-colors border-l-2 -ml-[2px] ${openSections[section.id] ? 'border-indigo-500 text-indigo-400 font-medium bg-indigo-950/10' : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'}`} data-id="element-6780">
                    {section.title}
                  </button>)}
              </nav>

              <div className="pt-4 flex gap-2" data-id="element-6781">
                <button onClick={expandAll} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-6782">
                  Expandir todo
                </button>
                <span className="text-slate-700" data-id="element-6783">•</span>
                <button onClick={collapseAll} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-id="element-6784">
                  Colapsar todo
                </button>
              </div>
            </div>
          </div>

          {/* Accordion Content */}
          <div className="lg:col-span-3 space-y-4 pb-20" data-id="element-6785">
            {TERMS_SECTIONS.map(section => {
            const Icon = section.icon;
            const isOpen = !!openSections[section.id];
            return <Card key={section.id} id={`term-section-${section.id}`} className={`bg-slate-900 border-slate-800 overflow-hidden transition-colors ${isOpen ? 'border-slate-700 shadow-md' : 'hover:border-slate-700'}`} data-id="element-6786">
                  <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none" data-id="element-6787">
                    <div className="flex items-center gap-3" data-id="element-6788">
                      <div className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-indigo-950/50 text-indigo-400' : 'bg-slate-800 text-slate-400'}`} data-id="element-6789">
                        <Icon size={18} data-id="element-6790" />
                      </div>
                      <h2 className={`text-lg font-semibold transition-colors ${isOpen ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-6791">
                        {section.title}
                      </h2>
                    </div>
                    <ChevronDown size={20} className={`text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-400' : ''}`} data-id="element-6792" />
                  </button>

                  <AnimatePresence initial={false} data-id="element-6793">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} data-id="element-6794">
                        <div className="px-5 pb-5 pt-2 border-t border-slate-800/50" data-id="element-6795">
                          {section.content}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </div>;
}